import { codeFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import FilePickerButton from '../shared/FilePickerButton';
import DataTable from '../shared/DataTable';
import DiscountGroupsSection from './DiscountGroupsSection';
import GenericMappingSection from './GenericMappingSection';
import VendorDialog from '../vendors/VendorDialog';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import { formatDate } from '../../utils';
import { useUserStore } from 'store';
import { formatPriceWithCurrency, priceForDisplay } from '../../utils/vat';

const API_INTEGRATION_NAMES = { orum: 'Örum', atoy: 'Atoy', meko: 'MEKO', ratparts: 'Rengastien Autotarvike' };

const POLL_INTERVAL = 2000;

// Feeds larger than this are gzipped client-side before upload to clear the CDN's
// request-size limit (Cloudflare caps uploads at 100 MB on non-Enterprise plans).
const GZIP_UPLOAD_THRESHOLD = 25 * 1024 * 1024;

const STATUS_COLORS = {
  pending: 'default',
  parsing: 'info',
  loading: 'info',
  resolving_prices: 'info',
  completed: 'success',
  failed: 'error',
};

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes > 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes > 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${bytes} B`;
}

const CatalogueDetailView = () => {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const showInclVat = currentOrganization?.default_price_includes_vat ?? false;
  const { id } = useParams();
  const navigate = useNavigate();
  const [catalogue, setCatalogue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingRole, setUploadingRole] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [discountOpened, setDiscountOpened] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [vendorEditOpen, setVendorEditOpen] = useState(false);
  const [vendorEditData, setVendorEditData] = useState(null);
  const pollRef = useRef(null);

  const showError = useCallback((message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  }, []);

  // Open the vendor edit dialog (e.g. to set the per-vendor parts margin). Fetch
  // the full vendor first — the catalogue carries only a slim vendor (id/name/margin)
  // and VendorDialog does a full PUT.
  const openVendorEdit = useCallback(async () => {
    const vendorId = catalogue?.vendor?.id;
    if (!vendorId) return;
    try {
      const res = await axios.get(`/api/vendors/${vendorId}/`);
      setVendorEditData(res.data);
      setVendorEditOpen(true);
    } catch {
      showError(t('catalogues.loadFailed'));
    }
  }, [catalogue, showError, t]);

  const hasImport = !!catalogue?.current_import;

  // Server-paginated items list (20/page). Only fetches once the catalogue is
  // imported; the DataTable drives page/page-size changes.
  const {
    data: items,
    loading: itemsLoading,
    page: itemsPage,
    pageSize: itemsPageSize,
    totalCount: itemsTotal,
    totalPages: itemsTotalPages,
    setPage: setItemsPage,
    setPageSize: setItemsPageSize,
    setSearchQuery: setItemsSearch,
    refetch: refetchItems,
  } = usePaginatedList(`/api/vendor-catalogues/${id}/items/`, {
    defaultPageSize: 20,
    enabled: hasImport,
  });

  const fetchCatalogue = useCallback(async () => {
    try {
      const response = await axios.get(`/api/vendor-catalogues/${id}/`);
      setCatalogue(response.data);
      return response.data;
    } catch {
      showError(t('catalogues.loadFailed'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [id, showError, t]);

  const runItemSearch = () => {
    setItemsSearch(itemSearch.trim());
  };

  // Tabs under the Tiedostot section: Nimikkeet (once imported) + Alennustaulukko
  // (discount-derived catalogues). Items is the default tab.
  const tabs = useMemo(() => {
    const list = [];
    if (hasImport) list.push('items');
    if (catalogue?.derives_price_from_discount) list.push('discount');
    return list;
  }, [hasImport, catalogue?.derives_price_from_discount]);

  // Clamp the active tab if the available set changes (e.g. after an import).
  useEffect(() => {
    if (activeTab >= tabs.length && tabs.length > 0) setActiveTab(0);
  }, [tabs, activeTab]);

  // The discount table loads only once its tab is (or becomes) active.
  useEffect(() => {
    if (tabs[activeTab] === 'discount') setDiscountOpened(true);
  }, [tabs, activeTab]);

  useEffect(() => {
    fetchCatalogue();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchCatalogue]);

  const startPolling = useCallback(
    (event) => {
      setActiveEvent(event);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const response = await axios.get(`/api/catalogue-import-events/${event.id}/`);
          setActiveEvent(response.data);
          if (['completed', 'failed'].includes(response.data.status)) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            fetchCatalogue();
            // New batch loaded — refresh the items list.
            refetchItems();
            setSnackbar({
              open: true,
              message:
                response.data.status === 'completed'
                  ? t('catalogues.importCompleted', { count: response.data.rows_loaded })
                  : t('catalogues.importFailed'),
              severity: response.data.status === 'completed' ? 'success' : 'error',
            });
          }
        } catch {
          // keep polling
        }
      }, POLL_INTERVAL);
    },
    [fetchCatalogue, refetchItems, t]
  );

  const handleUpload = async (role, file) => {
    setUploadingRole(role);
    try {
      // Large feeds (e.g. the ~148 MB atoy.dat) are gzipped in the browser so the
      // upload stays under the CDN's request-size limit (Cloudflare rejects >100 MB
      // at the edge). The backend decompresses transparently. atoy.dat is repetitive
      // fixed-width text and compresses ~5-10x. Falls back to a raw upload if the
      // browser lacks CompressionStream.
      let payload = file;
      let gzipped = false;
      if (file.size > GZIP_UPLOAD_THRESHOLD && typeof CompressionStream !== 'undefined') {
        payload = await new Response(file.stream().pipeThrough(new CompressionStream('gzip'))).blob();
        gzipped = true;
      }
      const formData = new FormData();
      formData.append('file', payload, file.name);
      formData.append('role', role);
      if (gzipped) formData.append('content_encoding', 'gzip');
      const response = await axios.post(`/api/vendor-catalogues/${id}/feeds/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCatalogue(response.data);
      setSnackbar({ open: true, message: t('catalogues.feedUploaded'), severity: 'success' });
    } catch (err) {
      showError(err.response?.data?.detail || t('catalogues.feedUploadFailed'));
    } finally {
      setUploadingRole(null);
    }
  };

  const handleImport = async () => {
    try {
      const response = await axios.post(`/api/vendor-catalogues/${id}/import/`);
      startPolling(response.data);
    } catch (err) {
      showError(err.response?.data?.detail || t('catalogues.importStartFailed'));
    }
  };

  const handleVatToggle = async (checked) => {
    try {
      const response = await axios.patch(`/api/vendor-catalogues/${id}/`, { prices_include_vat: checked });
      setCatalogue(response.data);
    } catch (err) {
      showError(err.response?.data?.detail || t('catalogues.vatToggleFailed'));
    }
  };

  const handleListPriceAsSellToggle = async (checked) => {
    try {
      const response = await axios.patch(`/api/vendor-catalogues/${id}/`, { list_price_is_sell_price: checked });
      setCatalogue(response.data);
    } catch (err) {
      showError(err.response?.data?.detail || t('catalogues.listPriceAsSellToggleFailed'));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/api/vendor-catalogues/${id}/`);
      navigate('/catalogues');
    } catch {
      showError(t('catalogues.deleteFailed'));
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!catalogue) {
    return null;
  }

  const feedsByRole = {};
  (catalogue.feeds || []).forEach((feed) => {
    feedsByRole[feed.role] = feed;
  });
  const requiredUploaded = (catalogue.required_feeds || [])
    .filter((spec) => spec.required)
    .every((spec) => spec.has_file);
  const isGeneric = ['generic_csv', 'generic_xlsx'].includes(catalogue.vendor_profile);
  const mappingConfigured = !isGeneric || Object.keys(catalogue.config?.field_mapping || {}).length > 0;
  const importRunning = activeEvent && !['completed', 'failed'].includes(activeEvent.status);

  return (
    <Box data-testid="catalogue-detail-page">
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/catalogues')} data-testid="back-to-catalogues">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {catalogue.name}
        </Typography>
        <Chip label={catalogue.vendor_profile_display} size="small" variant="outlined" />
        {!catalogue.is_active && <Chip label={t('catalogues.inactive')} size="small" color="default" />}
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={() => setConfirmDeleteOpen(true)} data-testid="delete-catalogue" title={t('Delete')}>
          <DeleteIcon />
        </IconButton>
      </Box>
      <Typography sx={{ color: 'text.secondary', mb: 1, ml: { xs: 0, sm: 6 } }}>
        {t('catalogues.vendor')}: {catalogue.vendor?.name} ·{' '}
        {catalogue.item_count
          ? t('catalogues.itemCountLabel', { count: catalogue.item_count })
          : t('catalogues.notImported')}
        {catalogue.last_imported_at && ` · ${t('catalogues.lastImported')}: ${formatDate(catalogue.last_imported_at)}`}
      </Typography>
      {/* Per-vendor parts margin (stored on the vendor; editable here too). */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3, ml: { xs: 0, sm: 6 } }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('catalogues.partsMargin')}:{' '}
          {catalogue.vendor?.parts_margin != null
            ? `${String(catalogue.vendor.parts_margin).replace('.', ',')} %`
            : t('catalogues.marginOrgDefault')}
        </Typography>
        <IconButton size="small" onClick={openVendorEdit} title={t('vendors.editVendor')} data-testid="edit-vendor-margin">
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Sähköpostitilaus: parts of this catalogue are orderable once the vendor has an order
          email (or an API integration). Offer to set it right here. */}
      {catalogue.vendor?.api_integration_type ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, ml: { xs: 0, sm: 6 } }} data-testid="catalogue-api-ordering">
          {t('catalogues.apiOrdering', {
            integration: API_INTEGRATION_NAMES[catalogue.vendor.api_integration_type] || catalogue.vendor.api_integration_type,
          })}
        </Typography>
      ) : catalogue.vendor?.order_email ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3, ml: { xs: 0, sm: 6 }, flexWrap: 'wrap' }} data-testid="catalogue-email-ordering-on">
          <EmailOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('catalogues.emailOrderingOn')}: {catalogue.vendor.order_email}
          </Typography>
          <IconButton size="small" onClick={openVendorEdit} title={t('vendors.editVendor')} data-testid="edit-vendor-order-email">
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ) : (
        <Alert
          severity="info"
          icon={<EmailOutlinedIcon fontSize="inherit" />}
          sx={{ mb: 3, ml: { xs: 0, sm: 6 }, alignItems: 'center' }}
          data-testid="catalogue-email-ordering-off"
          action={
            <Button color="inherit" size="small" onClick={openVendorEdit} data-testid="set-vendor-order-email">
              {t('catalogues.setOrderEmail')}
            </Button>
          }
        >
          {t('catalogues.emailOrderingOff')}
        </Alert>
      )}

      {/* Feed slots + import */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('catalogues.files')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleImport}
            disabled={!requiredUploaded || !mappingConfigured || importRunning}
            data-testid="import-catalogue-button"
          >
            {catalogue.current_import ? t('catalogues.refreshCatalogue') : t('catalogues.importCatalogue')}
          </Button>
        </Box>

        {isGeneric && !mappingConfigured && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('catalogues.configureMappingFirst')}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {(catalogue.required_feeds || []).map((spec) => {
            const feed = feedsByRole[spec.role];
            return (
              <Box
                key={spec.role}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '10px',
                }}
                data-testid={`feed-slot-${spec.role}`}
              >
                {feed ? (
                  <CheckCircleIcon sx={{ color: 'success.main' }} fontSize="small" />
                ) : spec.required ? (
                  <ErrorIcon sx={{ color: 'warning.main' }} fontSize="small" />
                ) : (
                  <UploadFileIcon sx={{ color: 'text.disabled' }} fontSize="small" />
                )}
                <Box sx={{ flex: 1, minWidth: 180 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    {spec.label}
                    {!spec.required && (
                      <Typography component="span" sx={{ fontSize: '12px', color: 'text.disabled', ml: 1 }}>
                        ({t('catalogues.optional')})
                      </Typography>
                    )}
                  </Typography>
                  {feed && (
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                      {feed.original_filename} · {formatBytes(feed.file_size_bytes)} ·{' '}
                      {formatDate(feed.uploaded_at)}
                    </Typography>
                  )}
                </Box>
                <FilePickerButton
                  onFiles={(files) => files[0] && handleUpload(spec.role, files[0])}
                  disabled={uploadingRole === spec.role || importRunning}
                  buttonProps={{
                    variant: 'outlined',
                    size: 'small',
                    startIcon: uploadingRole === spec.role ? <CircularProgress size={14} /> : <UploadFileIcon />,
                    'data-testid': `upload-feed-${spec.role}`,
                  }}
                >
                  {feed ? t('catalogues.replaceFile') : t('catalogues.uploadFile')}
                </FilePickerButton>
              </Box>
            );
          })}
        </Box>

        {/* VAT basis of the feed prices */}
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!catalogue.effective_prices_include_vat}
                onChange={(e) => handleVatToggle(e.target.checked)}
                data-testid="vat-included-toggle"
              />
            }
            label={t('catalogues.pricesIncludeVat')}
          />
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', ml: { xs: 0, sm: 6 } }}>
            {t('catalogues.pricesIncludeVatHelp')}
          </Typography>
        </Box>

        {/* Use the recommended retail (OVH/SVH) as the Part sell price.
            Only meaningful for feeds that carry a list price; off by default. */}
        {catalogue.has_list_price && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!catalogue.effective_list_price_is_sell_price}
                  onChange={(e) => handleListPriceAsSellToggle(e.target.checked)}
                  data-testid="list-price-as-sell-toggle"
                />
              }
              label={t('catalogues.useListPriceAsSell')}
            />
            <Typography sx={{ fontSize: '12px', color: 'text.secondary', ml: { xs: 0, sm: 6 } }}>
              {t('catalogues.useListPriceAsSellHelp')}
            </Typography>
          </Box>
        )}

        {/* Import progress */}
        {activeEvent && (
          <Box sx={{ mt: 2.5 }} data-testid="import-progress">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
              <Chip
                label={t(`catalogues.status_${activeEvent.status}`)}
                color={STATUS_COLORS[activeEvent.status] || 'default'}
                size="small"
                data-testid="import-status-chip"
              />
              {activeEvent.rows_loaded > 0 && (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                  {t('catalogues.rowsLoaded', { count: activeEvent.rows_loaded })}
                </Typography>
              )}
            </Box>
            {importRunning && <LinearProgress />}
            {activeEvent.status === 'failed' && activeEvent.error_detail && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {activeEvent.error_detail}
              </Alert>
            )}
            {activeEvent.status === 'completed' && activeEvent.error_detail && (
              <Alert severity="warning" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                {activeEvent.error_detail}
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* Generic mapping editor */}
      {isGeneric && (
        <GenericMappingSection
          catalogue={catalogue}
          onSaved={() => {
            fetchCatalogue();
            setSnackbar({ open: true, message: t('catalogues.mappingSaved'), severity: 'success' });
          }}
          onError={showError}
        />
      )}

      {/* Tabbed section under Tiedostot: Nimikkeet (default) + Alennustaulukko */}
      {tabs.length > 0 && (
        <Paper elevation={0} sx={{ mb: 3, overflow: 'hidden' }} data-testid="catalogue-tabs-section">
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 1, sm: 2 } }}>
            <Tabs
              value={Math.min(activeTab, tabs.length - 1)}
              onChange={(e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((key) => (
                <Tab
                  key={key}
                  data-testid={`catalogue-tab-${key}`}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                  label={
                    key === 'items'
                      ? `${t('catalogues.items')}${
                          catalogue.item_count ? ` (${catalogue.item_count.toLocaleString('fi-FI')})` : ''
                        }`
                      : t('catalogues.discountTable')
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* Nimikkeet — server-paginated (20/page) via DataTable */}
          {tabs[activeTab] === 'items' && (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <TextField
                  size="small"
                  placeholder={t('catalogues.searchItems')}
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') runItemSearch();
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton size="small" onClick={runItemSearch} data-testid="catalogue-items-search">
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    ),
                  }}
                />
              </Box>
              <DataTable
                columns={[
                  {
                    key: 'source_code',
                    label: t('catalogues.code'),
                    width: '1%',
                    noWrap: true,
                    render: (item) => (
                      <Typography sx={{ ...codeFont, fontSize: '12px' }}>{item.source_code}</Typography>
                    ),
                  },
                  {
                    key: 'brand',
                    label: t('catalogues.brand'),
                    render: (item) => <Typography sx={{ fontSize: '13px' }}>{item.brand || '-'}</Typography>,
                  },
                  {
                    key: 'name',
                    label: t('catalogues.itemName'),
                    maxWidth: 320,
                    render: (item) => <Typography sx={{ fontSize: '13px' }}>{item.name}</Typography>,
                  },
                  // Catalogue prices are always stored ex-VAT (VAT-inclusive feeds are
                  // normalized to net at import). They are shown in the organization's
                  // price display mode so they match the parts registry, and the header
                  // names the basis. Hidden for sell-price-only profiles (e.g. Avant).
                  ...((catalogue?.has_purchase_price ?? true)
                    ? [
                        {
                          key: 'purchase_price',
                          label: showInclVat ? t('catalogues.purchasePriceInclVat') : t('catalogues.purchasePriceExVat'),
                          width: '1%',
                          noWrap: true,
                          render: (item) => (
                            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                              {item.purchase_price != null
                                ? formatPriceWithCurrency(priceForDisplay(item.purchase_price, showInclVat))
                                : '-'}
                            </Typography>
                          ),
                        },
                      ]
                    : []),
                  // Recommended retail / list price (OVH/SVH) — only for profiles that carry one.
                  ...(catalogue?.has_list_price
                    ? [
                        {
                          key: 'list_price',
                          label: showInclVat ? t('catalogues.listPriceInclVat') : t('catalogues.listPriceExVat'),
                          width: '1%',
                          noWrap: true,
                          render: (item) => (
                            <Typography sx={{ fontSize: '13px' }}>
                              {item.list_price != null
                                ? formatPriceWithCurrency(priceForDisplay(item.list_price, showInclVat))
                                : '-'}
                            </Typography>
                          ),
                        },
                      ]
                    : []),
                  {
                    key: 'deposit',
                    label: t('catalogues.deposit'),
                    width: '1%',
                    noWrap: true,
                    render: (item) => (
                      <Typography sx={{ fontSize: '13px' }}>
                        {parseFloat(item.deposit) > 0 ? formatPriceWithCurrency(item.deposit) : '-'}
                      </Typography>
                    ),
                  },
                ]}
                data={items}
                loading={itemsLoading}
                emptyTitle={t('catalogues.noItemsFound')}
                testIdPrefix="catalogue-item-row"
                tableTestId="catalogue-items-table"
                alwaysVisible
                horizontalScroll
                pagination={{
                  page: itemsPage,
                  totalPages: itemsTotalPages,
                  totalCount: itemsTotal,
                  pageSize: itemsPageSize,
                  onPageChange: setItemsPage,
                  onPageSizeChange: setItemsPageSize,
                }}
              />
            </Box>
          )}

          {/* Alennustaulukko — mounted lazily once its tab is opened, then kept
              mounted (hidden) so it isn't refetched on every tab switch. */}
          {catalogue.derives_price_from_discount && discountOpened && (
            <Box sx={{ p: { xs: 2, sm: 3 }, display: tabs[activeTab] === 'discount' ? 'block' : 'none' }}>
              <DiscountGroupsSection catalogue={catalogue} onRecomputeStarted={startPolling} onError={showError} />
            </Box>
          )}
        </Paper>
      )}

      {/* Delete confirmation */}
      <Dialog open={confirmDeleteOpen} onClose={() => !deleting && setConfirmDeleteOpen(false)}>
        <DialogTitle>{t('catalogues.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('catalogues.deleteConfirmBody', { name: catalogue.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            data-testid="confirm-delete-catalogue"
          >
            {deleting ? <CircularProgress size={20} /> : t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vendor edit (e.g. the per-vendor parts margin), reused from the vendors view. */}
      <VendorDialog
        open={vendorEditOpen}
        handleClose={() => setVendorEditOpen(false)}
        vendor={vendorEditData}
        onUpdated={() => {
          setVendorEditOpen(false);
          fetchCatalogue();
          setSnackbar({ open: true, message: t('catalogues.vendorUpdated'), severity: 'success' });
        }}
      />
    </Box>
  );
};

export default CatalogueDetailView;
